import { CustomEventWithoutDetail, html, View } from 'rune-ts';
import {
  CreatorProductData,
  MODE,
  NewCreatorProductData,
} from '@services/MarppleShopStudio/widget/CreatorProduct/core/types/types.ts';
import { createCreatorProductStore } from '@services/MarppleShopStudio/widget/CreatorProduct/core/store/createStore.ts';
import { ButtonAction } from '@services/MarppleShop/shared/components/atoms/ButtonAction/ButtonAction.ts';
import klass from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/Form.module.scss';
import { each, pipe } from '@fxts/core';
import { doIf } from '@services/MarppleShop/shared/util';
import { ProductThumbnailForm } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductThumbnailForm/ProductThumbnailForm.ts';
import { makeMainUrl, makePlusUrl } from '@services/MarppleShop/shared/util/url.ts';
import { Modal } from '@services/MarppleShop/renderApp/components/atoms/Modal/Modal.ts';
import { isApp, navigateTo } from '@services/MarppleShop/shared/app/util.ts';
import { makeEmptyScreenNavigate } from '@services/MarppleShop/shared/app/navigate.ts';
import { shareFont } from '@services/MarppleShop/shared/typography';
import { ModalCloseIcon } from '@services/MarppleShop/renderApp/components/atoms/Icon';
import { CREW_DOMAIN_NAME } from '@modules/Creator/Crew/S/constant';
import { isNessApp } from '@modules/Util/S/Function/service_app_name';
import { getSharedData } from '@services/MarppleShop/shared/util/rune.ts';
import type { Locals } from '@services/MarppleShop/shared/type/locals.ts';
import { FormController } from './services/FormController.ts';
import { FormButtons } from './types.ts';
import { SeeMoreSettingButton } from '@shared/rune-component/Button/SeeMoreSettingButton/SeeMoreSettingButton.ts';

export type FormSectionCreator = (
  store: ReturnType<typeof createCreatorProductStore>,
  data: CreatorProductData,
) => View;

class EditorFormCloseEvent extends CustomEventWithoutDetail {}

export abstract class BaseForm<T extends CreatorProductData = NewCreatorProductData> extends View<T> {
  readonly thumbnailFormView = new ProductThumbnailForm(this.data);

  readonly actions = this.data.action;

  readonly store = createCreatorProductStore(this.data.mode, {
    ...this.data.stores_product,
    ...(this.isNewCreatorProductData(this.data)
      ? {}
      : {
          sp_cate_root_code: this.data.sp_cate_root_code,
          sp_cate_list_code: this.data.sp_cate_list_code,
          sp_cate_item_code: this.data.sp_cate_item_code,
        }),
  });

  readonly sections: View[] = this.createSections().map((sectionCreator) =>
    sectionCreator(this.store, this.data),
  );

  readonly buttons;
  readonly controller;

  protected constructor(data: T) {
    super({ ...data });

    this.controller = new FormController(this);
    this.buttons = pipe(this.createButtons(), (buttons) => ({
      header: buttons.header,
      footer: buttons.footer,
      seeMore: buttons.seeMore
        ? new SeeMoreSettingButton({ items: this.controller.createSeeMoreItems() })
        : null,
    }));
  }

  protected abstract createSections(): FormSectionCreator[];

  protected abstract createButtons(): FormButtons;

  protected override template() {
    return html`
      <div class="${klass.root}">
        ${doIf(() => this.renderMobileHeader(), this.data.is_mobile)}
        <div class="${klass.left_container}">${this.thumbnailFormView}</div>
        <div class="${klass.right_container}">
          <div class="${klass.form}">
            ${doIf(() => this.renderDesktopHeader(), !this.data.is_mobile)}
            <div class="${klass.section_container}">${this.sections.map((section) => section)}</div>
            ${doIf(() => this.renderDesktopFooter(), !this.data.is_mobile)}
          </div>
        </div>
        ${doIf(() => this.renderMobileFooter(), this.data.is_mobile)}
      </div>
    `;
  }

  override async onRender() {
    this.attachEventHandlers();
    await this.controller.checkNeedAdult();
  }

  /**
   * 이벤트 핸들러 등록
   */
  protected attachEventHandlers(): void {
    this.delegate('click', ButtonAction, async ({ target }) => {
      const button = target as HTMLElement;

      if (button.classList.contains(klass.button_save)) {
        this.handleButtonDisabled(true);
        await this.controller.save();
        this.handleButtonDisabled(false);
      }

      if (button.classList.contains(klass.button_temp_save)) {
        this.handleButtonDisabled(true);
        await this.controller.tempSave();
        this.handleButtonDisabled(false);
      }

      if (button.classList.contains(klass.button_preview)) {
        const url = this.buildPreviewUrl();
        navigateTo(url, makeEmptyScreenNavigate, { blank: true });
      }

      if (button.classList.contains(klass.button_close)) {
        this.dispatchEvent(BaseForm.EditorFormCloseEvent, { bubbles: true });
      }
    });

    this.delegate('click', `.${klass.close}`, () =>
      this.dispatchEvent(BaseForm.EditorFormCloseEvent, { bubbles: true }),
    );
  }

  /**
   * 미리보기 URL 빌드
   */
  protected buildPreviewUrl(): string {
    const productUrl = `${this.data.store_url}/products/${this.store.formData.product_id}`;

    if (isApp()) {
      return productUrl;
    }

    if (isNessApp()) {
      return `${window.location.origin}${productUrl}`;
    }

    return this.data.crew_domain_name !== CREW_DOMAIN_NAME.MARPPLE
      ? makePlusUrl(this.data.crew_domain_name, productUrl)
      : makeMainUrl(productUrl);
  }

  /**
   * 버튼 비활성화 처리
   */
  protected handleButtonDisabled(disabled: boolean): void {
    pipe(
      this.subViews(ButtonAction),
      each((button) => {
        if (disabled) button?.element?.().setAttribute('disabled', '');
        else button?.element?.().removeAttribute('disabled');
      }),
    );
  }

  /**
   * 모바일 헤더 렌더링
   */
  protected renderMobileHeader() {
    return html`
      <div class="${klass.title_container}">
        ${doIf(
          () => html`<div class="${klass.see_more}">${this.buttons.seeMore}</div>`,
          !!this.buttons.seeMore,
        )}
        <p class="${shareFont('20_bold', '16_bold')}">${this.getFormTitle()}</p>
        <div class="${klass.close}">
          <button type="button">${ModalCloseIcon()}</button>
        </div>
      </div>
    `;
  }

  /**
   * 데스크톱 헤더 렌더링
   */
  protected renderDesktopHeader() {
    return html`
      <section class="${klass.button_section}">
        ${this.buttons.header.map((button) => button)}
        ${doIf(() => this.buttons.seeMore!, !!this.buttons.seeMore)}
      </section>
    `;
  }

  /**
   * 데스크톱 푸터 렌더링
   */
  protected renderDesktopFooter() {
    return html`
      <section class="${klass.button_section}">${this.buttons.footer.map((button) => button)}</section>
    `;
  }

  /**
   * 모바일 푸터 렌더링
   */
  protected renderMobileFooter() {
    return html`
      <div
        class="${klass.footer}"
        ${this.tabBarPadding ? html`style="padding-bottom: ${this.tabBarPadding}px"` : ''}
      >
        ${this.buttons.header.map((button) => button)}
      </div>
    `;
  }

  /**
   * 폼 제목 반환
   */
  protected getFormTitle(): string {
    const titles = {
      [MODE.NEW]: ET('mps2::seller_studio::editor::pbg::new::title'),
      [MODE.EDIT]: ET('mps2::seller_studio::editor::pbg::modify::title'),
      [MODE.EDIT_TPL]: ET('mps2::seller_studio::editor::tpl::modify::title'),
      [MODE.EDIT_TPL_INTL]: ET('mps2::seller_studio::editor::tpl::modify::title'),
    };

    return titles[this.data.mode] || '';
  }

  /**
   * 탭바 패딩 계산
   */
  protected get tabBarPadding(): number {
    const webviewapp = getSharedData(this)?.webviewapp as Locals['webviewapp'];

    if (webviewapp?.tab_bar_height) {
      return webviewapp.tab_bar_height + Modal.FOOTER_PADDING;
    }

    return 0;
  }

  /**
   * NewCreatorProductData 타입 가드
   */
  protected isNewCreatorProductData(data: CreatorProductData): data is NewCreatorProductData {
    return data.mode === MODE.NEW;
  }

  static EditorFormCloseEvent = EditorFormCloseEvent;
}
